import React from 'react';
import type { PdfTemplateColors } from '../../types/pdfTemplate';

interface PdfColorSettingsProps {
  colors: PdfTemplateColors;
  onChange: (colors: PdfTemplateColors) => void;
}

const colorFields: Array<{ key: keyof PdfTemplateColors; label: string; description: string }> = [
  {
    key: 'brand',
    label: 'Brand Color',
    description: 'Primary brand color for headers and accents',
  },
  { key: 'textPrimary', label: 'Primary Text', description: 'Main body text color' },
  { key: 'textSecondary', label: 'Secondary Text', description: 'Labels, muted text' },
  { key: 'border', label: 'Border', description: 'Lines, dividers, and borders' },
  { key: 'background', label: 'Background', description: 'Page and section backgrounds' },
  {
    key: 'tableHeaderBg',
    label: 'Table Header Background',
    description: 'Table header row background',
  },
  { key: 'tableHeaderText', label: 'Table Header Text', description: 'Table header text color' },
];

const PdfColorSettings: React.FC<PdfColorSettingsProps> = ({ colors, onChange }) => {
  const update = (key: keyof PdfTemplateColors, value: string) => {
    onChange({ ...colors, [key]: value });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900">Color Settings</h3>
      <p className="text-xs text-gray-500">Customize the colors used throughout the PDF document</p>

      <div className="space-y-4">
        {colorFields.map(field => (
          <div key={field.key} className="flex items-center space-x-4">
            <input
              type="color"
              value={colors[field.key]}
              onChange={e => update(field.key, e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">{field.label}</label>
                <input
                  type="text"
                  value={colors[field.key]}
                  onChange={e => update(field.key, e.target.value)}
                  className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{field.description}</p>
            </div>
            <div
              className="w-16 h-8 rounded border border-gray-200"
              style={{ backgroundColor: colors[field.key] }}
            />
          </div>
        ))}
      </div>

      {/* Preview swatch row */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-2">Color Preview</p>
        <div className="flex space-x-2">
          {colorFields.map(field => (
            <div
              key={field.key}
              className="flex-1 h-8 rounded"
              style={{ backgroundColor: colors[field.key] }}
              title={field.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PdfColorSettings;
